/**
 * Mirror of `eduviaos-backend/app/domains/forms/{models,schemas}.py`.
 *
 * If a field is added/removed/renamed in the BE schema, edit this file in
 * the same PR (Iron Rule #11). Type drift between FE + BE is a merge blocker.
 */

export type FieldType =
  | "text"
  | "email"
  | "phone"
  | "number"
  | "currency"
  | "date"
  | "datetime"
  | "select"
  | "multi_select"
  | "radio"
  | "checkbox"
  | "textarea"
  | "address"
  | "file_upload"
  | "signature"
  | "section_header"
  | "help_text";

/** Field types that don't carry a submitted value. */
export const DISPLAY_ONLY_FIELD_TYPES: ReadonlySet<FieldType> = new Set([
  "section_header",
  "help_text",
]);

export interface FieldOption {
  value: string;
  label: string;
  color_hint?: string | null;
  icon?: string | null;
}

export type ConditionalOperator =
  | "eq"
  | "neq"
  | "in"
  | "not_in"
  | "gt"
  | "lt"
  | "exists"
  | "not_exists";

export interface ConditionalRule {
  field_key: string;
  operator: ConditionalOperator;
  value?: unknown;
}

export interface FieldValidation {
  required?: boolean;
  min_length?: number | null;
  max_length?: number | null;
  min_value?: number | null;
  max_value?: number | null;
  pattern?: string | null;
  pattern_message?: string | null;
  mime_types?: string[] | null;
  max_file_size_mb?: number | null;
  multiple_files?: boolean;
  options?: FieldOption[] | null;
  conditional_required?: ConditionalRule | null;
}

export interface FormField {
  key: string;
  label: string;
  field_type: FieldType;
  placeholder?: string | null;
  help_text?: string | null;
  default_value?: unknown;
  validation?: FieldValidation;
  visible_if?: ConditionalRule | null;
  grid_cols?: 1 | 2 | 3 | 4;
  order: number;
}

export interface FileRef {
  field_key: string;
  file_id: string;
  filename: string;
  mime_type: string;
  size_bytes: number;
}

export interface FormDefinition {
  id: string;
  tenant_id: string;
  name: string;
  slug: string;
  purpose: string;
  purpose_ref_id?: string | null;
  version: number;
  is_active_version: boolean;
  fields: FormField[];
  submit_button_label: string;
  success_message: string;
  requires_auth: boolean;
  created_at: string;
  created_by: string;
  updated_at: string;
  updated_by: string;
}

export interface FormCreateRequest {
  name: string;
  slug: string;
  purpose: string;
  purpose_ref_id?: string | null;
  fields: FormField[];
  submit_button_label?: string;
  success_message?: string;
  requires_auth?: boolean;
}

export interface FormUpdateRequest {
  name?: string;
  purpose?: string;
  purpose_ref_id?: string | null;
  fields?: FormField[];
  submit_button_label?: string;
  success_message?: string;
  requires_auth?: boolean;
}

export interface FormSubmitRequest {
  payload: Record<string, unknown>;
  file_refs?: FileRef[];
}

export interface FormSubmitResponse {
  submission_id: string;
  form_slug: string;
  form_version: number;
  submitted_at: string;
}

export interface FormSubmissionResponse {
  id: string;
  tenant_id: string;
  form_slug: string;
  form_version: number;
  submitted_by: string | null;
  payload: Record<string, unknown>;
  file_refs: FileRef[];
  submitted_at: string;
  ip_address: string | null;
  user_agent: string | null;
  referrer: string | null;
}

export interface FormListResponse {
  items: FormDefinition[];
  total: number;
}

export interface FormSubmissionListResponse {
  items: FormSubmissionResponse[];
  total: number;
}

export interface FieldValidationError {
  field_key: string;
  code: string;
  message: string;
}

/** All `FieldType` values in the order the FormBuilder palette renders them. */
export const FIELD_TYPE_ORDER: FieldType[] = [
  "text",
  "textarea",
  "email",
  "phone",
  "number",
  "currency",
  "date",
  "datetime",
  "select",
  "multi_select",
  "radio",
  "checkbox",
  "address",
  "file_upload",
  "signature",
  "section_header",
  "help_text",
];

/** Friendly label for the FormBuilder palette. */
export const FIELD_TYPE_LABEL: Record<FieldType, string> = {
  text: "Single-line text",
  textarea: "Long-form text",
  email: "Email",
  phone: "Phone",
  number: "Number",
  currency: "Currency",
  date: "Date",
  datetime: "Date & time",
  select: "Single select",
  multi_select: "Multi-select",
  radio: "Radio group",
  checkbox: "Checkbox group",
  address: "Address",
  file_upload: "File upload",
  signature: "Signature",
  section_header: "Section header",
  help_text: "Help text",
};
